import { applicationFeedbackMessage } from "./sourceChangeApplicationFeedback.js";

// Only these saved fields change a dispatched route; others stay with Mise à jour.
const DISPATCH_FIELDS = ["delivery_date", "delivery_period", "quantity_tons", "address", "client_code", "client_name",
  "loading_window_start", "loading_priority"];
const PERIOD_LABELS = { day: "Jour", evening: "Soir" };
const DATE_FORMATTER = new Intl.DateTimeFormat("fr-FR", { day: "numeric", month: "long" });

export const ORDER_SAVE_PROGRESS_STEPS = [
  { key: "save", label: "Enregistrement de la commande" },
  { key: "dispatch", label: "Actualisation du Dispatch" },
  { key: "refresh", label: "Actualisation de la table" },
];
export const ORDER_DELETE_PROGRESS_STEPS = [{ key: "save", label: "Suppression de la commande" }, ...ORDER_SAVE_PROGRESS_STEPS.slice(1)];

// The order editor sends the original values as expected_order_snapshot.
export function orderSaveTouchesDispatch(payload) {
  const original = payload?.expected_order_snapshot;
  // The unloading start and priority travel in the single editable delivery.
  const deliveryWindowChanged = ["window_start", "priority"]
    .some((field) => payload?.deliveries?.[0]?.[field] !== original?.deliveries?.[0]?.[field]);
  return Boolean(original) && (deliveryWindowChanged
    || DISPATCH_FIELDS.some((field) => field in payload && payload[field] !== original[field]));
}

// The Dispatch holding the order before its save, read from the save audit.
export function savedOrderDispatchTarget(result) {
  const audit = result?.audit;
  if (!audit?.changed_fields?.some((field) => DISPATCH_FIELDS.includes(field) || field === "deliveries")) return null;
  const before = (field) => {
    const change = audit.field_changes?.find((item) => item?.field === field);
    return change ? change.old_value : result?.order?.[field];
  };
  const dispatchDate = String(before("delivery_date") || "").slice(0, 10);
  if (!dispatchDate) return null;
  return {
    dispatchDate,
    dispatchPeriod: before("delivery_period") === "evening" ? "evening" : "day",
    affectedDispatchDates: [...new Set([dispatchDate, String(result.order?.delivery_date || "").slice(0, 10)].filter(Boolean))],
  };
}

function dayLabel(entry) {
  const [year, month, day] = String(entry.dispatch_date || "").split("-").map(Number);
  const date = year ? DATE_FORMATTER.format(new Date(year, month - 1, day, 12)) : entry.dispatch_date;
  return `${date} (${PERIOD_LABELS[entry.dispatch_period] || "Jour"})`;
}

function dispatchLabel(entry) {
  return `Dispatch du ${dayLabel(entry)}${entry.truck_label ? ` · Camion ${entry.truck_label}` : ""}`;
}

export async function prepareSavedOrderDispatch({ result, prepareOrder, reportProgress, setCompletionToast, updateLoadingToast }) {
  const target = savedOrderDispatchTarget(result);
  if (!target) {
    reportProgress("dispatch", "skipped");
    return result;
  }
  // A deleted order only leaves its dispatch.
  const orderId = result.order?.order_id || result.deleted_id;
  const done = result.order ? "enregistrée" : "supprimée";
  reportProgress("dispatch");
  updateLoadingToast({ title: "Actualisation du Dispatch", message: `Commande ${orderId} ${done}. Calcul du Dispatch…` });
  const messages = [];
  let warning = false;
  try {
    const preparation = await prepareOrder(orderId, { ...target, expectedUpdatedAt: result.order?.updated_at });
    if (!Array.isArray(preparation?.results)) throw new Error("Le résultat du Dispatch est incomplet.");
    for (const entry of preparation.results) {
      warning ||= entry.outcome === "blocked";
      const message = entry.outcome === "blocked" ? applicationFeedbackMessage(entry.application_feedback) || entry.message : entry.message;
      messages.push(`${dispatchLabel(entry)} : ${message}`);
    }
    if (preparation.moved_to) {
      messages.push(`Elle apparaît dans « Nouvelles cmd vers Suspendues » du ${dayLabel(preparation.moved_to)}.`);
    }
  } catch (error) {
    // A lost response can follow a commit: Mise à jour shows the real state, never repeat the POST.
    warning = true;
    messages.push(error?.message || "Résultat du Dispatch indisponible.");
  }
  reportProgress("dispatch", warning ? "warning" : "completed");
  setCompletionToast({
    title: `Commande ${done} — Dispatch ${warning ? "à vérifier" : "actualisé"}`,
    message: `${messages.join(" ")}${warning ? " Consultez Mise à jour." : ""}`,
    type: warning ? "warning" : "success",
    duration: warning ? 12000 : 8000,
  });
  return { ...result, _data_manager_success_toast_handled: true, _prepared_dispatch: target, _dispatch_warning: warning };
}
